"use client"

import { ListFilter } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useState } from "react"
import type { TooltipProps } from "recharts"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { type ChartConfig, ChartContainer, ChartLegend, ChartTooltip } from "@/components/ui/chart"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface StackedBarChartProps {
  title: string
  description?: string
  data: Array<Record<string, string | number>>
  categoryKey: string
  stackKeys: string[]
  theme?: ThemeConfigType
  className?: string
}

// Custom tooltip that filters out zero values
const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload?.length) {
    return null
  }

  // Filter out entries with zero or undefined values
  const filteredPayload = payload.filter((entry) => entry.value !== undefined && entry.value !== 0)

  if (filteredPayload.length === 0) {
    return null
  }

  return (
    <div className="border-border/50 bg-background rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <div className="font-medium mb-1.5">{label}</div>
      <div className="grid gap-1.5">
        {filteredPayload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-2">
            <div
              className="h-2.5 w-2.5 shrink-0 rounded-xs"
              style={{ backgroundColor: entry.color }}
            />
            <div className="flex flex-1 justify-between leading-none items-center">
              <span className="text-muted-foreground">{entry.name}</span>
              <span className="text-foreground font-mono font-medium tabular-nums ml-4">
                {entry.value?.toLocaleString()}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export const StackedBarChart = observer(
  ({
    title,
    description,
    data,
    categoryKey,
    stackKeys,
    theme,
    className,
  }: StackedBarChartProps) => {
    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme

    // State to track which stacks are visible
    const [visibleStacks, setVisibleStacks] = useState<Set<string>>(new Set(stackKeys))

    // Sanitize keys to make them valid CSS variable names
    // Replace special characters with hyphens and ensure uniqueness
    const sanitizeKey = (key: string): string => {
      return key
        .replace(/[^a-zA-Z0-9-_]/g, "-")
        .replace(/^[0-9]/, "n$&") // Prefix numbers with 'n'
        .toLowerCase()
    }

    // Create mapping of original keys to sanitized keys
    const keyMap = new Map<string, string>()
    const sanitizedKeys = stackKeys.map((key) => {
      const sanitized = sanitizeKey(key)
      keyMap.set(key, sanitized)
      return sanitized
    })

    // Extended color palette for better variety when there are many stacks
    // Using CSS variables that are defined in index.css
    const colorPalette = [
      "var(--chart-1)", // Orange
      "var(--chart-2)", // Teal
      "var(--chart-3)", // Blue
      "var(--chart-4)", // Yellow
      "var(--chart-5)", // Light Orange
      // Add more distinct colors using HSL for better variety
      "hsl(280 65% 60%)", // Purple
      "hsl(340 75% 55%)", // Pink
      "hsl(160 60% 50%)", // Green
      "hsl(30 80% 55%)", // Dark Orange
      "hsl(200 70% 50%)", // Bright Blue
      "hsl(120 50% 50%)", // Lime
      "hsl(300 55% 60%)", // Magenta
    ]

    // Create chart config with colors for each SANITIZED stack key
    // This is what ChartStyle uses to create CSS variables
    // IMPORTANT: We need to add config for BOTH sanitized and original keys
    // - Sanitized keys: for ChartStyle to create CSS variables
    // - Original keys: for custom legend/dropdown to find labels and colors
    const chartConfig: ChartConfig = sanitizedKeys.reduce((config, sanitizedKey, index) => {
      const originalKey = stackKeys[index]
      const colorValue = colorPalette[index % colorPalette.length]

      // Add config for sanitized key (for CSS variables)
      config[sanitizedKey] = {
        label: originalKey, // Use original label for display
        color: colorValue,
      }

      // Also add config for original key (for legend/dropdown lookup)
      // This ensures our custom components can find the label and color
      if (sanitizedKey !== originalKey) {
        config[originalKey] = {
          label: originalKey,
          color: colorValue,
        }
      }

      return config
    }, {} as ChartConfig)

    // Toggle function for stack visibility
    const toggleStack = (key: string) => {
      setVisibleStacks((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(key)) {
          newSet.delete(key)
        } else {
          newSet.add(key)
        }
        return newSet
      })
    }

    // Determine if we should show legend below (≤5 stacks) or dropdown (>5 stacks)
    const showLegendBelow = stackKeys.length <= 5

    // Custom clickable legend component for ≤5 stacks
    const ClickableLegend = ({
      payload,
    }: {
      payload?: Array<{ value: string; color: string; type?: string }>
    }) => {
      if (!payload?.length) {
        return null
      }

      return (
        <div className="flex items-center justify-center gap-4 flex-wrap pt-3">
          {payload.map((item) => {
            const key = item.value
            const itemConfig = chartConfig[key]
            const isVisible = visibleStacks.has(key)

            return (
              <button
                type="button"
                key={item.value}
                onClick={() => toggleStack(key)}
                className={cn(
                  "flex items-center gap-1.5 cursor-pointer transition-opacity",
                  !isVisible && "opacity-40",
                )}
              >
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{
                    backgroundColor: item.color,
                  }}
                />
                <span className="text-sm">{itemConfig?.label || item.value}</span>
              </button>
            )
          })}
        </div>
      )
    }

    return (
      <Card
        className={cn(
          "w-full overflow-hidden",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
          <div className="space-y-1">
            <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>
              {title}
            </CardTitle>
            {description && (
              <p
                className={cn(
                  "text-sm text-muted-foreground",
                  !isDefaultTheme && themeClasses.typography,
                )}
              >
                {description}
              </p>
            )}
          </div>

          {/* Show dropdown menu for >5 stacks */}
          {!showLegendBelow && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 gap-1">
                  <ListFilter className="h-3.5 w-3.5" />
                  <span className="sr-only sm:not-sr-only">Filter</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuLabel>Toggle Stacks</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {stackKeys.map((key) => {
                  const itemConfig = chartConfig[key]
                  const colorValue = colorPalette[stackKeys.indexOf(key) % colorPalette.length]

                  return (
                    <DropdownMenuCheckboxItem
                      key={key}
                      checked={visibleStacks.has(key)}
                      onCheckedChange={() => toggleStack(key)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2 w-2 shrink-0 rounded-[2px]"
                          style={{
                            backgroundColor: colorValue,
                          }}
                        />
                        <span>{itemConfig?.label || key}</span>
                      </div>
                    </DropdownMenuCheckboxItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={chartConfig}
            className="h-[300px] sm:h-[350px] md:h-[400px] w-full"
          >
            <BarChart data={data} accessibilityLayer>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey={categoryKey}
                tickLine={false}
                tickMargin={8}
                axisLine={false}
                tick={{ fontSize: "12px" }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tickLine={false} axisLine={false} tick={{ fontSize: "12px" }} />
              <ChartTooltip content={<CustomTooltip />} />

              {/* Only show legend for ≤5 stacks */}
              {showLegendBelow && (
                <ChartLegend
                  content={<ClickableLegend />}
                  wrapperStyle={{
                    paddingTop: "20px",
                  }}
                  layout="horizontal"
                  verticalAlign="bottom"
                />
              )}

              {/* Render only visible stacks */}
              {stackKeys
                .filter((key) => visibleStacks.has(key))
                .map((key) => {
                  const sanitizedKey = keyMap.get(key) || sanitizeKey(key)
                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="a"
                      fill={`var(--color-${sanitizedKey})`}
                    />
                  )
                })}
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    )
  },
)

StackedBarChart.displayName = "StackedBarChart"
